import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Tab, Tabs, Typography, Grid } from '@mui/material';
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import PlayerRankMockCard from "../../components/PlayerRankMockCard/PlayerRankMockCard";
import MaterialReactTable from 'material-react-table';
import moment from 'moment';
import PlayerDraftOdd from "../../components/PlayerDraftOdd/PlayerDraftOdd";
import { publicApi } from "../../api/http";
import PlayerCard from "../../components/PlayerCard/PlayerCard";
import PlayerGraph from "../../components/PlayerGraph/PlayerGraph";
import { Container } from "@mui/system";
import { goalieColumns, playerColumns } from "./Constants.js";

import "./Player.css"

function Player() {
    const [playerData, setPlayerData] = useState(null);
    const [playerStats, setPlayerStats] = useState(null);

    const [graphData, setGraphData] = useState(null);
    const [lastConsensusData, setLastConsensusData] = useState(null);
    const [playerRankMocks, setPlayerRankMocks] = useState(null);
    const [playerDraftProbabilities, setPlayerDraftProbabilities] = useState([]);
    const [playerFilm, setPlayerFilm] = useState([]);
    const [activeTab, setActiveTab] = useState("consensus");
    const [activeFilmKey, setActiveFilmKey] = useState("");
    const [playerCardHeight, setPlayerCardHeight] = useState(null);
    const playerCardRef = useRef(null);
    const { player_id } = useParams();

    useEffect(() => {
        publicApi.get("/players/" + player_id).then((response) => {
            setPlayerData(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerData([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/player_stats/" + player_id).then((response) => {
            setPlayerStats(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerStats([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/player_draft_probabilities/player/" + player_id).then((response) => {
            setPlayerDraftProbabilities(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerDraftProbabilities([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/film/" + player_id).then((response) => {
            setPlayerFilm(Array.isArray(response.data) ? response.data : []);
        })
            .catch((error) => {
                console.error(error);
                setPlayerFilm([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/shared/get_player_mocks_and_rankings/" + player_id).then((response) => {
            setPlayerRankMocks(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerRankMocks([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/consensus_rankings/" + player_id).then((response) => {
            setLastConsensusData(response.data[response.data.length - 1]);
            return response.data
        }).then((re) => {
            const scores = re.map(item => Math.round(item.score * 1e2) / 1e2);
            const effectiveDates = re.map(item => moment(item.effective_date).valueOf());
            const lastRank = re.map(item => ((item.has_data_point) ? item.last_rank : null));
            const consensusRanking = re.map(item => item.rank);
            const lastRankType = re.map(item => item.last_rank_type);

            // Create an array of objects combining the effective date, score, and rank
            setGraphData(re.map((item, index) => ({
                effective_date: effectiveDates[index],
                score: scores[index],
                lastRank: lastRank[index],
                consensusRanking: consensusRanking[index],
                lastRankType: lastRankType[index]
            })));
        })
            .catch((error) => {
                console.error(error);
            });
    }, [player_id]);

    let darkTheme = createTheme({
        palette: {
            mode: 'dark',
            info: {
                main: '#7ea862',
                secondary: '#7ea862',
            },
            background: {
                default: '#14181d',
                paper: '#14181d',
            },
            text: {
                primary: '#f3f4f6',
                secondary: '#a1a8b3',
            },
        },
        shadows: ["none"]
    });

    const playerColumnsMemo = useMemo(
        () => playerColumns, [],
    );

    const goalieColumnsMemo = useMemo(
        () => goalieColumns, [],
    );

    // e.g. draft_year=2026 → "2025-26" (regular season + playoffs)
    const filteredPlayerStats = useMemo(() => {
        if (!playerStats || !playerData?.draft_year) return playerStats;
        const draftYear = playerData.draft_year;
        const targetSeason = `${draftYear - 1}-${String(draftYear).slice(-2)}`;
        return playerStats.filter(stat => stat.year === targetSeason);
    }, [playerStats, playerData]);

    const hasFilm = playerFilm && playerFilm.length > 0;

    const getFilmKey = (film) => film?.id || film?.url || "";

    const getYouTubeVideoId = (url) => {
        if (!url) return "";

        try {
            const parsedUrl = new URL(url);
            const videoId = parsedUrl.searchParams.get("v");
            if (videoId) {
                return videoId;
            }

            const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
            if (parsedUrl.hostname.includes("youtu.be")) {
                return pathParts[0] || "";
            }
            if (pathParts[0] === "embed" || pathParts[0] === "shorts") {
                return pathParts[1] || "";
            }
        } catch {
            const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([^&?/]+)/);
            return match?.[1] || "";
        }

        return "";
    };

    useEffect(() => {
        if (activeTab === "film" && !hasFilm) {
            setActiveTab("consensus");
        }
    }, [activeTab, hasFilm]);

    useEffect(() => {
        if (!hasFilm) {
            setActiveFilmKey("");
            return;
        }

        if (!playerFilm.some((film) => getFilmKey(film) === activeFilmKey)) {
            setActiveFilmKey(getFilmKey(playerFilm[0]));
        }
    }, [activeFilmKey, hasFilm, playerFilm]);

    useEffect(() => {
        if (!playerCardRef.current) {
            return undefined;
        }

        const updatePlayerCardHeight = () => {
            const nextHeight = playerCardRef.current?.getBoundingClientRect().height;
            if (nextHeight) {
                setPlayerCardHeight(nextHeight);
            }
        };

        updatePlayerCardHeight();

        if (!window.ResizeObserver) {
            window.addEventListener("resize", updatePlayerCardHeight);
            return () => window.removeEventListener("resize", updatePlayerCardHeight);
        }

        const observer = new ResizeObserver(updatePlayerCardHeight);
        observer.observe(playerCardRef.current);
        return () => observer.disconnect();
    }, [playerData]);

    const getYouTubeEmbedUrl = (url) => {
        if (!url) return "";
        const videoId = getYouTubeVideoId(url);
        return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    };

    const activeFilm = useMemo(
        () => playerFilm.find((film) => getFilmKey(film) === activeFilmKey) || playerFilm[0] || null,
        [activeFilmKey, playerFilm]
    );

    return (
        <div>
            {playerData && (
                <Helmet>
                    <title>{playerData.first_name} {playerData.last_name} - Hockey Pipeline</title>
                    <meta name="description" content={`Track ${playerData.first_name} ${playerData.last_name}'s NHL prospect journey with detailed statistics, rankings, mock drafts, and player analysis on Hockey Pipeline.`} />
                    <link rel="canonical" href={`https://hockeypipeline.com/player/${player_id}`} />
                    <meta property="og:title" content={`${playerData.first_name} ${playerData.last_name} - Hockey Pipeline`} />
                    <meta property="og:description" content={`Track ${playerData.first_name} ${playerData.last_name}'s NHL prospect journey with detailed statistics and rankings.`} />
                    <meta property="og:url" content={`https://hockeypipeline.com/player/${player_id}`} />
                </Helmet>
            )}
            {/* PLAYER CARD AND TABBED SUMMARY */}
            <Container style={{ marginTop: 40 }}>
                <Grid container spacing={2} className="player-hero-grid">
                    <Grid item xs={12} sm={12} md={3} className="player-card-column">
                        <div className="player-card-measure" ref={playerCardRef}>
                            {playerData && <PlayerCard playerData={playerData} />}
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={9} className="player-tabs-column">
                        <div
                            className="container player-tabs-container"
                            style={playerCardHeight ? { "--player-card-height": `${playerCardHeight}px` } : undefined}
                        >
                            <Tabs
                                value={activeTab}
                                onChange={(event, value) => setActiveTab(value)}
                                className="player-tabs"
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                <Tab label="Consensus Rankings" value="consensus" />
                                <Tab label="Stats" value="stats" />
                                {hasFilm && <Tab label="Film" value="film" />}
                            </Tabs>

                            <Box className="player-tab-panel" hidden={activeTab !== "consensus"}>
                                {lastConsensusData && (
                                    <>
                                        {graphData && <PlayerGraph graphData={graphData} />}
                                        <Grid container spacing={2} className="rank-stat-grid">
                                            <Grid item xs={6} sm={6} md={12 / 5} >
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Number of Rankings:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.count}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Highest Ranking:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.best_rank}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Lowest Ranking:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.worst_rank}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Last Ranking:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.last_rank}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Standard Deviation:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {Math.round(lastConsensusData.std_dev * 1e2) / 1e2}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                                {!lastConsensusData && (
                                    <Typography variant="body1" component="div" className="empty-tab-message">
                                        No consensus ranking history available for this player.
                                    </Typography>
                                )}
                            </Box>

                            <Box className="player-tab-panel" hidden={activeTab !== "stats"}>
                                <Typography variant="h4" component="div" className="tab-section-title">
                                    Draft Year Stats
                                </Typography>
                                {playerData && filteredPlayerStats && (
                                    <>
                                        <ThemeProvider theme={darkTheme}>
                                            <MaterialReactTable
                                                columns={(playerData.position === "G") ? goalieColumnsMemo : playerColumnsMemo}
                                                data={filteredPlayerStats}
                                                initialState={{ density: 'compact', pagination: { pageSize: 10 } }}
                                            />
                                        </ThemeProvider>
                                        {filteredPlayerStats.length === 0 && (
                                            <Typography variant="body1" component="div" className="no-stats-message">
                                                No stats recorded for the player's draft year.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>

                            {hasFilm && (
                                <Box className="player-tab-panel film-tab-panel" hidden={activeTab !== "film"}>
                                    <Grid container spacing={2} className="film-browser">
                                        <Grid item xs={12} md={3} className="film-list-column">
                                            <div className="film-list">
                                                {playerFilm.map((film) => {
                                                    const filmKey = getFilmKey(film);
                                                    const videoId = getYouTubeVideoId(film.url);
                                                    const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : "";
                                                    const isActiveFilm = getFilmKey(activeFilm) === filmKey;

                                                    return (
                                                        <button
                                                            type="button"
                                                            key={filmKey}
                                                            className={`film-list-item${isActiveFilm ? " film-list-item--active" : ""}`}
                                                            onClick={() => setActiveFilmKey(filmKey)}
                                                        >
                                                            {thumbnailUrl && (
                                                                <img
                                                                    src={thumbnailUrl}
                                                                    alt=""
                                                                    className="film-list-thumbnail"
                                                                />
                                                            )}
                                                            <span className="film-list-title">
                                                                {film.title || "Player Film"}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} md={9} className="film-active-column">
                                            {activeFilm && (
                                                <div className="film-active-card">
                                                    <div className="film-active-frame">
                                                        <iframe
                                                            width="100%"
                                                            height="100%"
                                                            src={getYouTubeEmbedUrl(activeFilm.url)}
                                                            title={activeFilm.title || "Player film"}
                                                            frameBorder="0"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                            allowFullScreen
                                                        />
                                                    </div>
                                                    <Typography variant="body1" component="div" className="film-title">
                                                        {activeFilm.title || "Player Film"}
                                                    </Typography>
                                                </div>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </Container>

            {playerDraftProbabilities && playerDraftProbabilities.length > 0 && <Container >
                <Grid container columnSpacing={2} style={{ marginTop: 20 }}>
                    {playerDraftProbabilities && <Grid item xs={12} sm={12} md={12}>
                        <div className="container con-no-bot-pad player-draft-odds">
                            <div className="draft-odds">
                                <Typography variant="h4" component="div" className="draft-odds-title">
                                    Draft Odds
                                </Typography>
                                <Grid container columnSpacing={0}>
                                    <Grid item xs={12} sm={12} md={4}>
                                        {playerDraftProbabilities && playerDraftProbabilities.slice(0, playerDraftProbabilities.length / 3)
                                            .map((draftProb) => (
                                                <PlayerDraftOdd draftProb={draftProb} />
                                            ))
                                        }
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={4}>
                                        {playerDraftProbabilities && playerDraftProbabilities.slice(playerDraftProbabilities.length / 3, (playerDraftProbabilities.length / 3) * 2)
                                            .map((draftProb) => (
                                                <PlayerDraftOdd draftProb={draftProb} />
                                            ))
                                        }
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={4}>
                                        {playerDraftProbabilities && playerDraftProbabilities.slice((playerDraftProbabilities.length / 3) * 2, playerDraftProbabilities.length)
                                            .map((draftProb) => (
                                                <PlayerDraftOdd draftProb={draftProb} />
                                            ))
                                        }
                                    </Grid>
                                </Grid>
                            </div>
                        </div>
                    </Grid>}
                </Grid>
            </Container>}


            {/* PLAYER RANKINGS */}
            <Container spacing={2} style={{ marginTop: 40 }} className="container" >
                <Grid container >
                    <Grid item xs={12} sm={12} md={12} >
                        <Typography variant="h4" component="div" className="page-title">
                            All Rankings
                        </Typography>
                        <div className="title-underline" />
                    </Grid>
                    {playerRankMocks && playerRankMocks.map((playerRankMock) => (
                        <Grid item xs={12} sm={12} md={12} >
                            <PlayerRankMockCard playerRankMock={playerRankMock} />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </div>
    );
};

export default Player;
